import { useMemo, useState } from 'react';
import Input from '../ui/Input';

/**
 * Shared grouped/searchable permission checkbox matrix.
 *
 * mode="role": single-state — `value` (array of names) / `onChange(nextArray)`, all editable
 *   (unless `readOnly`, e.g. the super-admin role).
 * mode="user": three-state — permissions inherited via `rolePermissions` are checked + locked;
 *   `directPermissions` / `onDirectChange(nextArray)` control the remaining editable grants.
 */
export default function PermissionMatrix({
  mode,
  groups,
  value = [],
  onChange,
  rolePermissions = [],
  directPermissions = [],
  onDirectChange,
  readOnly = false,
  roleNames = [],
}) {
  const [search, setSearch] = useState('');

  const roleSet = useMemo(() => new Set(rolePermissions), [rolePermissions]);
  const directSet = useMemo(() => new Set(directPermissions), [directPermissions]);
  const valueSet = useMemo(() => new Set(value), [value]);

  const isChecked = (name) => (mode === 'user' ? roleSet.has(name) || directSet.has(name) : valueSet.has(name));
  const isInherited = (name) => mode === 'user' && roleSet.has(name);
  const isLocked = (name) => readOnly || isInherited(name);

  const toggle = (name) => {
    if (isLocked(name)) return;
    if (mode === 'user') {
      const next = directSet.has(name) ? directPermissions.filter((p) => p !== name) : [...directPermissions, name];
      onDirectChange(next);
    } else {
      const next = valueSet.has(name) ? value.filter((p) => p !== name) : [...value, name];
      onChange(next);
    }
  };

  const toggleGroup = (perms, checked) => {
    if (readOnly) return;
    const names = perms.map((p) => p.name);
    if (mode === 'user') {
      const editableNames = names.filter((n) => !roleSet.has(n));
      const next = checked
        ? Array.from(new Set([...directPermissions, ...editableNames]))
        : directPermissions.filter((p) => !editableNames.includes(p));
      onDirectChange(next);
    } else {
      const next = checked ? Array.from(new Set([...value, ...names])) : value.filter((p) => !names.includes(p));
      onChange(next);
    }
  };

  const matches = (perm) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return perm.name.toLowerCase().includes(q) || perm.label.toLowerCase().includes(q);
  };

  const filteredGroups = Object.entries(groups)
    .map(([group, perms]) => [group, perms.filter(matches)])
    .filter(([, perms]) => perms.length > 0);

  const effectiveCount = mode === 'user' ? new Set([...roleSet, ...directSet]).size : value.length;

  return (
    <div>
      {mode === 'user' && (
        <p className="mb-4 text-sm text-gray-600">
          Effective permissions: <span className="font-semibold text-gray-900">{effectiveCount}</span> (
          {roleSet.size} from roles, {directSet.size} direct)
        </p>
      )}

      <Input
        placeholder="Search permissions…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="mb-4"
      />

      <div className="space-y-6">
        {filteredGroups.map(([group, perms]) => {
          const editablePerms = mode === 'user' ? perms.filter((p) => !roleSet.has(p.name)) : perms;
          const allEditableChecked = editablePerms.length > 0 && editablePerms.every((p) => isChecked(p.name));

          return (
            <div key={group}>
              <div className="flex items-center justify-between border-b border-gray-100 pb-1.5 mb-2">
                <h3 className="text-sm font-semibold text-gray-900">{group}</h3>
                {!readOnly && editablePerms.length > 0 && (
                  <label className="flex items-center gap-1.5 text-xs text-gray-500 cursor-pointer">
                    <input
                      type="checkbox"
                      className="accent-primary"
                      checked={allEditableChecked}
                      onChange={(e) => toggleGroup(editablePerms, e.target.checked)}
                    />
                    Select all{mode === 'user' ? ' direct' : ''}
                  </label>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5">
                {perms.map((perm) => {
                  const locked = isLocked(perm.name);
                  const checked = isChecked(perm.name);
                  const direct = mode === 'user' && directSet.has(perm.name) && !roleSet.has(perm.name);

                  return (
                    <label
                      key={perm.name}
                      title={isInherited(perm.name) ? `Inherited via role: ${roleNames.join(', ') || 'role'}` : undefined}
                      className={`flex items-center gap-2 rounded px-2 py-1 text-sm ${
                        locked ? 'text-gray-500 cursor-not-allowed' : 'text-gray-700 cursor-pointer hover:bg-gray-50'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={locked}
                        onChange={() => toggle(perm.name)}
                        className={locked ? 'accent-gray-400' : 'accent-primary'}
                      />
                      <span className={direct ? 'font-medium text-primary' : ''}>{perm.label}</span>
                      {isInherited(perm.name) && (
                        <span className="ml-auto rounded bg-gray-100 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-gray-500">
                          role
                        </span>
                      )}
                    </label>
                  );
                })}
              </div>
            </div>
          );
        })}

        {filteredGroups.length === 0 && <p className="text-sm text-gray-500">No permissions match your search.</p>}
      </div>
    </div>
  );
}
